import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { FullCalendarComponent } from '@fullcalendar/angular';
import { Calendar, CalendarOptions } from '@fullcalendar/core';
import bootstrap5Plugin from '@fullcalendar/bootstrap5';
import interactionPlugin from '@fullcalendar/interaction';
import timeGridPlugin from '@fullcalendar/timegrid';
import { OAuthService } from 'angular-oauth2-oidc';
import moment from 'moment';
import 'moment/locale/hu';
import { firstValueFrom } from 'rxjs';
import { DataService } from '../data.service';
import { esemenyCimHtml } from '../naptar/tanevrendje-tipusok';
import { kulsoLinkMegnyit } from '../beadandok/beadando-kozos';

@Component({
  selector: 'app-szuloi-orarend',
  templateUrl: './szuloi-orarend.component.html',
  styleUrls: ['./szuloi-orarend.component.scss'],
})
export class SzuloiOrarendComponent implements OnInit, AfterViewInit, OnDestroy {
  cal?: FullCalendarComponent;
  @ViewChild(FullCalendarComponent, { static: false })
  set calendar(component: FullCalendarComponent | undefined) {
    if (!component) return;
    this.cal = component;
    this.onCalendarReady();
  }

  user$: any;
  sajatIntezmeny: any;
  gyerekek: any[] = [];
  kivtanulo: string | null = null;
  /*  Ha a TANULÓ lépett be, ez a saját sora – a legördülő helyett a nevét írjuk ki. */
  sajatSor: any = null;
  calapi?: Calendar;
  csengetes: any[] = [];
  tankezd: any;
  tanvege: any;
  headerTitle = '';
  loading = false;
  uresOrarend = false;
  /*  NEM TANÓRAI CÉLÚ FOGLALKOZÁSOK (korrepetálás, napközi, szakkör): a kiszolgáló alapból
   *  kihagyja őket, és a válaszban megmondja, van-e ilyen foglalkozása a gyermeknek
   *  (`van`), látszanak-e (`lathato`), hány maradt ki a héten (`rejtett`), és a belépő
   *  állíthatja-e (`modosithato` – csak a gondviselő, a tanuló nem). */
  nemTanorai: { van: boolean; lathato: boolean; rejtett: number; modosithato: boolean } | null = null;
  calendarReady = false;
  calendarInitInProgress = false;
  view: 'workWeek' | 'workWeekSat' = 'workWeek';

  /*  ONLINE ÓRÁK (digitális munkarend, backend/beadando.js): a tanár a naplóban az órához
   *  rögzített linkje. A hét óráinál „Online óra” felirat, fölöttük egy lista „Csatlakozás”
   *  gombbal, ami az óra idején (15 perccel előtte a végéig) aktív. */
  onlineLinkek = new Map<string, string>();
  onlineOrak: { id: string; cim: string; kezd: Date; veg: Date; link: string }[] = [];
  most = Date.now();
  private oraIdozito: any = null;
  private static readonly ONLINE_ELOTTE_PERC = 15;

  calendarOptions: CalendarOptions = {
    plugins: [timeGridPlugin, interactionPlugin, bootstrap5Plugin],
    initialView: 'workWeek',
    locale: 'hu',
    height: 'auto',
    themeSystem: 'standard',
    firstDay: 1,
    headerToolbar: false,
    allDaySlot: true,
    allDayText: 'Események',
    slotMinTime: '00:00:00',
    slotMaxTime: '10:00:00',
    slotDuration: '00:15:00',
    slotLabelInterval: '01:00:00',
    slotLabelContent: (arg) => this.oraFelirat(arg.date.getHours()),
    displayEventEnd: false,
    eventTimeFormat: { hour: '2-digit', minute: '2-digit', hour12: false },
    eventBackgroundColor: 'var(--ion-color-primary)',
    eventBorderColor: 'var(--ion-color-primary-shade)',
    eventTextColor: 'var(--ion-color-primary-contrast)',
    eventClick: (arg: any) => this.onlineKattintas({ id: arg?.event?.id }),
    eventContent: (arg) => {
      const lines = arg.event.title.split('\n').map((line) => `<div>${line}</div>`).join('');
      return { html: lines };
    },
    datesSet: (arg: any) => {
      if (arg?.view?.currentStart && arg?.view?.currentEnd) {
        this.headerTitle = this.buildTitle(arg.view.currentStart, arg.view.currentEnd);
      }
    },
    views: {
      workWeek: {
        type: 'timeGridWeek',
        weekends: false,
        dayHeaderContent: (arg) => this.dayHeader(arg.date),
      },
      workWeekSat: {
        type: 'timeGridWeek',
        hiddenDays: [0],
        dayHeaderContent: (arg) => this.dayHeader(arg.date),
      },
    },
    events: [],
  };

  constructor(
    private http: HttpClient,
    private oauth: OAuthService,
    private data: DataService
  ) {}

  async ngOnInit() {
    await this.oauth.loadUserProfile();
    this.user$ = this.data.belepoKilet(this.oauth.getIdentityClaims());
    this.sajatIntezmeny = await this.data.getFromStorage('sajatIntezmeny');
    await this.resolveTanev();
    await this.loadGyerekek();
  }

  async ngAfterViewInit() {
    if (this.cal) await this.onCalendarReady();
    //  A „Csatlakozás” gomb az óra idején aktív: félpercenként frissül a „most”.
    this.oraIdozito = setInterval(() => { this.most = Date.now(); }, 30000);
  }

  ngOnDestroy() {
    if (this.oraIdozito) clearInterval(this.oraIdozito);
  }

  /** A hét óráinak online linkjei a kiválasztott gyermekhez (ora_id → link). */
  private async onlineLinkekBetolt(tanulo: string): Promise<Map<string, string>> {
    const ki = new Map<string, string>();
    if (!this.calapi || !tanulo || !this.sajatIntezmeny?.intezmenyId) return ki;
    const tol = moment(this.calapi.view.currentStart).format('YYYY-MM-DD');
    const ig = moment(this.calapi.view.currentEnd).subtract(1, 'day').format('YYYY-MM-DD');
    try {
      const r: any = await firstValueFrom(this.http.get(
        `/api/beadando/tanulo/${encodeURIComponent(tanulo)}/online/${this.sajatIntezmeny.intezmenyId}?tol=${tol}&ig=${ig}`));
      for (const x of (Array.isArray(r) ? r : [])) if (x?.ora_id && x?.link) ki.set(String(x.ora_id), String(x.link));
    } catch {
      // Az online linkek nem vihetik magukkal az órarendet: ha nem jönnek, csak kimaradnak.
    }
    return ki;
  }

  csatlakozhato(o: { kezd: Date; veg: Date }): boolean {
    const elotte = SzuloiOrarendComponent.ONLINE_ELOTTE_PERC * 60000;
    return this.most >= o.kezd.getTime() - elotte && this.most <= o.veg.getTime();
  }

  get latszoOnlineOrak() {
    return this.onlineOrak.filter((o) => o.veg.getTime() >= this.most).sort((a, b) => a.kezd.getTime() - b.kezd.getTime());
  }

  onlineIdo(o: { kezd: Date; veg: Date }): string {
    return `${moment(o.kezd).locale('hu').format('dddd HH:mm')}–${moment(o.veg).format('HH:mm')}`;
  }

  csatlakozas(o: { link: string }) { kulsoLinkMegnyit(o.link); }

  /** Kattintás egy órára a naptárban: ha online óra, és épp most van, csatlakozunk. */
  private onlineKattintas(ora: any) {
    const o = this.onlineOrak.find((x) => x.id === String(ora?.id || ''));
    if (!o) return;
    if (this.csatlakozhato(o)) { this.csatlakozas(o); return; }
    this.data.sendToast(`Online óra: a csatlakozás az óra idején (${this.onlineIdo(o)}) nyílik meg.`, 'info');
  }

  async resolveTanev() {
    const tanev = await this.data.getFromStorage('tanev');
    const tanevadatok = await this.data.getFromStorage('tanevadatok');

    try {
      const aktiv = tanev ? JSON.parse(tanev) : null;
      const lista = tanevadatok ? JSON.parse(tanevadatok) : [];
      const aktualis = (lista || []).find((item: any) => item.id == aktiv?.aktualistanev);
      this.tankezd = aktualis?.tankezd;
      this.tanvege = aktualis?.tanvege;
    } catch (error) {
      console.error('Tanév feloldási hiba:', error);
    }
  }

  /**
   *  Az óratengely felirata: az óraszám alatt a csengetési rend (kezdés–vég).
   *  A naptár az óraszámot óra-értékként használja, ezért abból keressük ki a sort.
   *  Amíg a csengetési rend nem érkezett meg, csak az óraszám látszik.
   */
  private oraFelirat(oraszam: number): any {
    const sor: any = (this.csengetes || [])
      .find((x: any) => Number(x?.oraszam ?? x?.ora) === Number(oraszam));
    if (!sor?.kezdes) return `${oraszam}. óra`;
    const ido = (v: any) => String(v || '').slice(0, 5);
    return { html: `${oraszam}. óra<small class="csengetes-ido">${ido(sor.kezdes)}–${ido(sor.vege)}</small>` };
  }

  async configureCalendar() {
    if (!this.calapi || !this.sajatIntezmeny?.intezmenyId) return;

    try {
      this.csengetes = await this.data.getCsengetes(
        moment(this.tankezd || new Date()).format('YYYY'),
        this.sajatIntezmeny.intezmenyId
      ).toPromise();
      const utolsoOra = Math.max(...(this.csengetes || []).map((item: any) => Number(item?.oraszam || item?.ora || 0)), 10);
      this.calapi.setOption('slotMaxTime', String(utolsoOra + 1).padStart(2, '0') + ':00:00');
      // A tengely feliratai a naptár létrehozásakor már kirajzolódtak, a csengetési rend
      // viszont csak most érkezett meg – újra kell építeni őket, különben csak az
      // óraszám maradna.
      this.calapi.setOption('slotLabelContent', (arg: any) => this.oraFelirat(arg.date.getHours()));
    } catch (error) {
      console.error('Csengetési rend betöltési hiba:', error);
    }

    if (this.tankezd && moment().isBefore(moment(this.tankezd))) {
      this.calapi.gotoDate(moment(this.tankezd).toDate());
    }
  }

  async onCalendarReady() {
    if (!this.cal || this.calendarInitInProgress) return;
    this.calendarInitInProgress = true;
    this.calapi = this.cal.getApi();

    setTimeout(async () => {
      if (!this.calapi) {
        this.calendarInitInProgress = false;
        return;
      }
      await this.configureCalendar();
      this.calendarReady = true;
      this.emptyHet(this.isoWeekNumber(this.calapi!.view.currentStart), this.calapi!.view.currentStart);
      this.calapi?.render();
      this.calendarInitInProgress = false;
    }, 0);
  }

  async loadGyerekek() {
    if (!this.user$?.username || !this.sajatIntezmeny?.intezmenyId) return;

    await this.data.showLoading('Gyermekek betöltése...');
    this.http
      .get<any[]>(`/api/nyilvantartas/szulogyereke/${this.user$.username}/${this.sajatIntezmeny.intezmenyId}`)
      .subscribe({
        next: async (response: any[] = []) => {
          //  A felügyeleti jogot nem gyakorló szülő (felugyeleti_jog jelölés) az órarendet nem látja.
          this.gyerekek = (Array.isArray(response) ? response : []).filter((g: any) => !g?.felugyeleti_jog);
          this.sajatSor = this.data.sajatTanulo(this.gyerekek);
          if (this.sajatSor) this.kivtanulo = this.sajatSor.oktazon;
          else if (!this.kivtanulo && this.gyerekek.length) {
            this.kivtanulo = this.gyerekek[0].oktazon;
          }
          await this.data.hideLoading();
          if (this.calendarReady) {
            this.emptyHet(this.isoWeekNumber(this.calapi!.view.currentStart), this.calapi!.view.currentStart);
          }
        },
        error: async (error) => {
          console.error('Gyermeklista lekérési hiba:', error);
          await this.data.hideLoading();
          this.data.sendToast('Nem sikerült betölteni a gyermeklistát.', 'error');
        },
      });
  }

  /**
   *  A nem tanórai foglalkozások ki-bekapcsolása a kiválasztott gyermekre. A beállítás a
   *  GYERMEKÉ: a gyermek saját belépésén és a másik gondviselőnél is érvényes.
   */
  async nemTanoraiValt(lathato: boolean) {
    const allapot = this.nemTanorai;
    const oktazon = this.kivtanulo;
    if (!allapot || !oktazon || !this.user$?.username || !!lathato === !!allapot.lathato) return;
    const elozo = allapot.lathato;
    allapot.lathato = !!lathato;
    try {
      await firstValueFrom(this.http.put(
        `/api/nem-tanorai/${encodeURIComponent(this.user$.username)}/${encodeURIComponent(oktazon)}`,
        { lathato: !!lathato }));
      if (this.calendarReady && this.calapi) {
        this.emptyHet(this.isoWeekNumber(this.calapi.view.currentStart), this.calapi.view.currentStart);
      }
    } catch (e: any) {
      allapot.lathato = elozo;
      this.data.sendToast(e?.error?.error || 'A beállítás nem menthető – próbáld újra.', 'error');
    }
  }

  onTanuloValtozas(event: any) {
    this.kivtanulo = event?.oktazon ?? event ?? null;
    this.nemTanorai = null;
    if (this.calendarReady) {
      this.emptyHet(this.isoWeekNumber(this.calapi!.view.currentStart), this.calapi!.view.currentStart);
    }
  }

  emptyHet(weekNum: number, start: Date) {
    if (!this.calapi) return;

    this.calapi.removeAllEvents();
    this.getHetOrak(weekNum);
  }

  getHetOrak(het: number) {
    if (!this.calapi || !this.kivtanulo || !this.user$?.username || !this.sajatIntezmeny?.intezmenyId) return;

    const currentStart = this.calapi.view.currentStart || new Date();
    const ev = moment(currentStart).format('YYYY');

    this.loading = true;
    this.uresOrarend = false;
    const kertTanulo = String(this.kivtanulo);
    this.http
      .get<any>(`/api/orarend/gondviselo/${this.user$.username}/${this.kivtanulo}/${ev}/${het}/${this.sajatIntezmeny.intezmenyId}`)
      .subscribe({
        next: (response: any = {}) => {
          const orak = Array.isArray(response?.orak) ? response.orak : [];
          // A kapcsoló a gyermekre szól: egy korábban kiválasztott gyermek későn érkező
          // válasza nem írhatja át a most kiválasztottét.
          if (kertTanulo === String(this.kivtanulo)) this.nemTanorai = response?.nem_tanorai || null;
          this.uresOrarend = orak.length === 0;
          this.onlineLinkek = new Map();
          this.renderOrak(orak);
          this.loading = false;
          //  Az online linkek utólag jönnek; ha van, az órák felirata és a lista frissül.
          if (orak.length) {
            this.onlineLinkekBetolt(kertTanulo).then((linkek) => {
              if (kertTanulo !== String(this.kivtanulo) || !linkek.size) return;
              this.onlineLinkek = linkek;
              this.renderOrak(orak);
            });
          }
        },
        error: (error) => {
          console.error('Szülői órarend lekérési hiba:', error);
          this.renderOrak([]);
          this.loading = false;
          this.data.sendToast('Nem sikerült betölteni az órarendet.', 'error');
        },
      });
  }

  /**
   *  Naptári programok az „Események" (egész napos) sorba.
   *
   *  A szűrés a SZERVEREN történik: a szülő csak a publikált, a gyerekére vonatkozó
   *  eseményt kapja meg (intézményi, illetve az ő osztályára/csoportjára szólót).
   *  A gyerek azonosítója megy fel, mert a hatókör az ő besorolásából jön.
   */
  private async naptariProgramok() {
    if (!this.calapi || !this.sajatIntezmeny?.intezmenyId || !this.kivtanulo) return;
    const tol = moment(this.calapi.view.currentStart).format('YYYY-MM-DD');
    const ig = moment(this.calapi.view.currentEnd).subtract(1, 'day').format('YYYY-MM-DD');
    try {
      const p = new URLSearchParams({ tol, ig, mod: 'gondviselo', tanulo: String(this.kivtanulo) });
      const r: any = await firstValueFrom(this.http
        .get(`/api/tanevrendje/orarend/${this.sajatIntezmeny.intezmenyId}?${p.toString()}`));
      for (const e of (r?.esemenyek || [])) {
        const kezd = moment(e.kezdes);
        const veg = e.vege ? moment(e.vege) : kezd.clone();
        this.calapi.addEvent({
          id: 'tkt-' + e.id,
          // A vége FullCalendarban kizárólagos: a több napos program utolsó napjához +1 nap kell.
          title: esemenyCimHtml(e.ikon, e.nev),
          start: kezd.format('YYYY-MM-DD'),
          end: veg.clone().add(1, 'day').format('YYYY-MM-DD'),
          allDay: true,
          backgroundColor: '#6b7fd7',
          borderColor: '#5468b8',
          textColor: '#fff',
        });
      }
    } catch (err) {
      // Az eseménysáv nem viheti magával az órarendet: ha nem jön, csak kimarad.
      console.warn('Naptári programok betöltése sikertelen:', err);
    }
  }

  renderOrak(orak: any[]) {
    if (!this.calapi) return;

    this.calapi.removeAllEvents();
    this.onlineOrak = [];
    for (const ora of orak || []) {
      const datum = moment(ora?.datum);
      if (!datum.isValid()) continue;

      const oraSorszam = Number(ora?.ora);
      const hossz = Number(ora?.hossz || 1);
      if (!Number.isFinite(oraSorszam)) continue;

      const start = `${datum.format('YYYY-MM-DD')}T${String(oraSorszam).padStart(2, '0')}:00:00`;
      const end = `${datum.format('YYYY-MM-DD')}T${String(oraSorszam + hossz).padStart(2, '0')}:00:00`;

      const osztalyVagyCsoport = ora.osztaly || ora.csoport_nev || '';
      const title =
        `${ora.tantargy || '-'} - ${osztalyVagyCsoport}`.trim() +
        (ora.helyettesito ? ` (${ora.helyettesites_tipus || ''}-${ora.helyettesites_mod || ''})<s>` : '') +
        //  Az elmaradt óra önálló állapot (orarendv2.elmaradt, 2026-10-15) – a családnak is látszik,
        //  a tartó tanár áthúzva, mint a régi „E” módú óránál (a felirat magában nem elég feltűnő).
        (Number(ora.elmaradt) === 1 ? ' – ELMARAD' : '') +
        (Number(ora.elmaradt) === 1 && !ora.helyettesito ? '<s>' : '') +
        `\n${ora.tarto || ''}${ora.tartokod ? ` (${ora.tartokod})` : ''}\n` +
        (ora.helyettesito ? `</s>${ora.helyettesito}${ora.helyettesitokod ? ` (${ora.helyettesitokod})` : ''}\n`
          : (Number(ora.elmaradt) === 1 ? '</s>' : '')) +
        `${ora.terem || ''}` +
        (this.onlineLinkek.has(String(ora.id)) ? '\n<b>Online óra</b>' : '');
      const onlineLink = this.onlineLinkek.get(String(ora.id));
      if (onlineLink) {
        const kezd = this.getOraDate(ora, 'tol');
        const veg = this.getOraDate({ ...ora, ora: Number(ora.ora) + Math.max(1, Number(ora.hossz || 1)) - 1 }, 'ig');
        if (kezd && veg) {
          this.onlineOrak.push({ id: String(ora.id), cim: `${ora.ora}. óra · ${ora.tantargy || '-'}`, kezd, veg, link: onlineLink });
        }
      }
      const multbeliOra = datum.isBefore(moment());
      const backgroundColor = multbeliOra
        ? ((!ora.helyettesito ? ora.tartoszin : '#ff0095ff') || 'var(--ion-color-primary)')
        : 'grey';
      const borderColor = multbeliOra
        ? (ora.helyettesito ? '#fccdfe' : 'var(--ion-color-primary-shade)')
        : 'grey';
      const textColor = multbeliOra ? 'var(--ion-color-primary-contrast)' : 'white';

      this.calapi.addEvent({
        id: String(ora.id),
        title,
        start,
        end,
        backgroundColor,
        borderColor,
        textColor,
        extendedProps: {
          ...ora,
          originalBg: backgroundColor,
          originalBorder: borderColor,
          originalText: textColor,
          isSelected: false,
        },
      });
    }
    this.naptariProgramok().then(() => this.calapi?.render());
    this.calapi.render();
  }

  getOraDate(ora: any, tipus: 'tol' | 'ig'): Date | null {
    const datum = moment(ora?.datum);
    if (!datum.isValid()) return null;

    const csengetes = (this.csengetes || []).find((item: any) => Number(item?.oraszam ?? item?.ora) === Number(ora?.ora));
    const ido = tipus === 'tol'
      ? (csengetes?.kezdes || csengetes?.tol || `${String(ora?.ora || 0).padStart(2, '0')}:00`)
      : (csengetes?.vege || csengetes?.ig || `${String((Number(ora?.ora) || 0) + 1).padStart(2, '0')}:00`);
    const idoString = String(ido).length === 5 ? `${ido}:00` : String(ido);
    return moment(`${datum.format('YYYY-MM-DD')} ${idoString}`, 'YYYY-MM-DD HH:mm:ss').toDate();
  }

  get kivalasztottGyerek(): any {
    return (this.gyerekek || []).find((gyerek: any) => String(gyerek?.oktazon) === String(this.kivtanulo));
  }

  prev() {
    if (!this.calapi) return;
    this.calapi.prev();
    this.emptyHet(this.isoWeekNumber(this.calapi.view.currentStart), this.calapi.view.currentStart);
  }

  next() {
    if (!this.calapi) return;
    this.calapi.next();
    this.emptyHet(this.isoWeekNumber(this.calapi.view.currentStart), this.calapi.view.currentStart);
  }

  today() {
    if (!this.calapi) return;
    this.calapi.today();
    this.emptyHet(this.isoWeekNumber(this.calapi.view.currentStart), this.calapi.view.currentStart);
  }

  changeView(view: 'workWeek' | 'workWeekSat') {
    this.view = view;
    if (!this.calapi) return;
    this.calapi.changeView(view);
    this.emptyHet(this.isoWeekNumber(this.calapi.view.currentStart), this.calapi.view.currentStart);
  }

  buildTitle(start: Date, end: Date): string {
    const s = moment(start).locale('hu');
    const e = moment(end).subtract(1, 'day').locale('hu');
    if (s.month() === e.month()) return `${s.format('YYYY. MMMM D.')} - ${e.format('D.')}`;
    return `${s.format('YYYY. MMMM D.')} - ${e.format('MMMM D.')}`;
  }

  dayHeader(date: Date) {
    return {
      html: `${date.toLocaleString('hu-HU', { weekday: 'long' })}<br>${date.toLocaleString('hu-HU', { month: 'long' })} ${date.getDate()}.`,
    };
  }

  isoWeekNumber(d: Date) {
    const tmp = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
    const day = tmp.getUTCDay() || 7;
    tmp.setUTCDate(tmp.getUTCDate() + 4 - day);
    const yearStart = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1));
    return Math.ceil((((tmp as any) - (yearStart as any)) / 86400000 + 1) / 7);
  }
}
